/**
 * Inline translations for the public help shell.
 *
 * Following the existing EZDocu pattern: the dashboard uses a typed
 * Translations system in `lib/i18n/`, but landing / legal / public pages
 * keep small inline maps so they don't pull the whole dashboard i18n
 * graph for half a dozen strings. Help pages match that pattern.
 */
export const HELP_LOCALES = ['en', 'es', 'pt', 'de', 'fr'] as const;
export type HelpLocale = (typeof HELP_LOCALES)[number];

export const HELP_LANGUAGES: { code: HelpLocale; flag: string; label: string }[] = [
  { code: 'en', flag: '🇺🇸', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'pt', flag: '🇧🇷', label: 'Português' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
];

interface HelpStrings {
  backToHome: string;
  helpCenter: string;
  signIn: string;
  goToDashboard: string;
  language: string;
  related: string;
  lastUpdated: string;
  backToUpdates: string;
  /** `<title>` del índice; la plantilla del layout raíz ya añade «| EZDocuAI». */
  metaTitle: string;
  metaDescription: string;
  heading: string;
  /** Sin mencionar el widget del asistente: /help no lo monta. */
  subtitle: string;
  /** El índice solo lista los artículos del idioma de la URL. */
  empty: string;
  /** Categoría de respaldo para los artículos sin categoría en el CMS. */
  generalCategory: string;
  videoTutorial: string;
  notFoundTitle: string;
}

export const HELP_T: Record<HelpLocale, HelpStrings> = {
  en: {
    backToHome: 'Back to EZDocuAI',
    helpCenter: 'Help center',
    signIn: 'Sign in',
    goToDashboard: 'Open dashboard',
    language: 'Language',
    related: 'Related',
    lastUpdated: 'Last updated',
    backToUpdates: 'Back to Updates',
    metaTitle: 'Help center: guides and FAQ',
    metaDescription: 'Step-by-step guides and answers to common questions about EZDocuAI: uploading documents, orders, credits, glossaries and certificates.',
    heading: 'How can we help you?',
    subtitle: 'Quick guides on how to use EZDocuAI, organized by topic.',
    empty: 'There are no articles published in this language yet.',
    generalCategory: 'General',
    videoTutorial: 'Video tutorial',
    notFoundTitle: 'Article not found',
  },
  es: {
    backToHome: 'Volver a EZDocuAI',
    helpCenter: 'Centro de ayuda',
    signIn: 'Iniciar sesión',
    goToDashboard: 'Ir al panel',
    language: 'Idioma',
    related: 'Relacionados',
    lastUpdated: 'Última actualización',
    backToUpdates: 'Volver a Novedades',
    metaTitle: 'Centro de ayuda: guías y preguntas frecuentes',
    metaDescription: 'Guías paso a paso y respuestas a las preguntas frecuentes sobre EZDocuAI: subir documentos, órdenes, créditos, glosarios y certificados.',
    heading: '¿Cómo podemos ayudarte?',
    subtitle: 'Guías rápidas sobre cómo usar EZDocuAI, organizadas por tema.',
    empty: 'Todavía no hay artículos publicados en este idioma.',
    generalCategory: 'General',
    videoTutorial: 'Tutorial en video',
    notFoundTitle: 'Artículo no encontrado',
  },
  pt: {
    backToHome: 'Voltar ao EZDocuAI',
    helpCenter: 'Central de ajuda',
    signIn: 'Entrar',
    goToDashboard: 'Abrir painel',
    language: 'Idioma',
    related: 'Relacionados',
    lastUpdated: 'Última atualização',
    backToUpdates: 'Voltar às Novidades',
    metaTitle: 'Central de ajuda: guias e perguntas frequentes',
    metaDescription: 'Guias passo a passo e respostas às perguntas frequentes sobre o EZDocuAI: envio de documentos, pedidos, créditos, glossários e certificados.',
    heading: 'Como podemos ajudar?',
    subtitle: 'Guias rápidos sobre como usar o EZDocuAI, organizados por tema.',
    empty: 'Ainda não há artigos publicados neste idioma.',
    generalCategory: 'Geral',
    videoTutorial: 'Tutorial em vídeo',
    notFoundTitle: 'Artigo não encontrado',
  },
  de: {
    backToHome: 'Zurück zu EZDocuAI',
    helpCenter: 'Hilfe-Center',
    signIn: 'Anmelden',
    goToDashboard: 'Zum Dashboard',
    language: 'Sprache',
    related: 'Verwandt',
    lastUpdated: 'Zuletzt aktualisiert',
    backToUpdates: 'Zurück zu Neuigkeiten',
    metaTitle: 'Hilfe-Center: Anleitungen und häufige Fragen',
    metaDescription: 'Schritt-für-Schritt-Anleitungen und Antworten auf häufige Fragen zu EZDocuAI: Dokumente hochladen, Aufträge, Credits, Glossare und Zertifikate.',
    heading: 'Wie können wir Ihnen helfen?',
    subtitle: 'Kurze Anleitungen zur Nutzung von EZDocuAI, nach Themen geordnet.',
    empty: 'In dieser Sprache sind noch keine Artikel veröffentlicht.',
    generalCategory: 'Allgemein',
    videoTutorial: 'Video-Anleitung',
    notFoundTitle: 'Artikel nicht gefunden',
  },
  fr: {
    backToHome: 'Retour à EZDocuAI',
    helpCenter: "Centre d'aide",
    signIn: 'Se connecter',
    goToDashboard: 'Ouvrir le tableau de bord',
    language: 'Langue',
    related: 'Articles liés',
    lastUpdated: 'Dernière mise à jour',
    backToUpdates: 'Retour aux Nouveautés',
    metaTitle: "Centre d'aide : guides et questions fréquentes",
    metaDescription: 'Guides pas à pas et réponses aux questions fréquentes sur EZDocuAI : envoi de documents, commandes, crédits, glossaires et certificats.',
    heading: 'Comment pouvons-nous vous aider ?',
    subtitle: 'Des guides rapides pour utiliser EZDocuAI, classés par thème.',
    empty: "Aucun article n'a encore été publié dans cette langue.",
    generalCategory: 'Général',
    videoTutorial: 'Tutoriel vidéo',
    notFoundTitle: 'Article introuvable',
  },
};
